import { ArrowLeft, Loader2 } from "lucide-react";
import { QuizActionButton } from "@/components/storyboard/quiz/QuizActionButton";
import { retryLabel } from "@/lib/validationSubmission";
import type { ValidationResult } from "@/services/QuizValidationService";

/** The slice of {@link useQuizCodeState} the action row drives itself from. */
export interface CodeQuizActionsState {
  currentIndex: number;
  currentResult?: ValidationResult;
  isSettled: boolean;
  isMultiple: boolean;
  isLast: boolean;
  progressLabel: string | false;
  isValidating: boolean;
  error: string | null;
  handleSubmit: () => void;
  handleNext: () => void;
  handleBack: () => void;
}

interface CodeQuizActionsProps {
  quiz: CodeQuizActionsState;
  /** CTA wording for a first try, e.g. "Code prüfen" / "Erklärung prüfen". */
  submitLabel: string;
  /** False while the input is empty — nothing to hand to the validator. */
  canSubmit: boolean;
  /** Button style of the task navigation, per quiz type's surrounding chrome. */
  navVariant?: "outline" | "secondary";
}

/**
 * Footer row shared by the code quizzes: task navigation on the left, the
 * submit CTA on the right.
 *
 * After a wrong answer the CTA becomes "Erneut versuchen". The attempt count
 * stays in the left pane, not on the button. Once the task is right or out of
 * attempts the CTA disappears, and the overlay's own control closes the quiz.
 */
export const CodeQuizActions = ({
  quiz,
  submitLabel,
  canSubmit,
  navVariant = "outline",
}: CodeQuizActionsProps) => {
  const {
    currentIndex,
    currentResult,
    isSettled,
    isMultiple,
    isLast,
    progressLabel,
    isValidating,
    error,
  } = quiz;

  const label = currentResult ? retryLabel() : submitLabel;

  return (
    <>
      {isMultiple && (
        <div className="mr-auto flex items-center gap-3">
          <span className="font-semibold text-gray-500 tabular-nums">
            {progressLabel}
          </span>
          {currentIndex > 0 && (
            <QuizActionButton
              onClick={quiz.handleBack}
              disabled={isValidating}
              withArrow={false}
              variant={navVariant}
              size="default"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Zurück
            </QuizActionButton>
          )}
          {!isLast && (
            <QuizActionButton
              onClick={quiz.handleNext}
              disabled={isValidating}
              variant={navVariant}
              size="default"
            >
              Nächste Frage
            </QuizActionButton>
          )}
        </div>
      )}
      {error && <p className="mr-auto text-error-foreground">{error}</p>}
      {!isSettled && (
        <QuizActionButton
          variant="default"
          onClick={quiz.handleSubmit}
          disabled={isValidating || !canSubmit}
          withArrow={false}
        >
          {isValidating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Wird geprüft …
            </>
          ) : (
            label
          )}
        </QuizActionButton>
      )}
    </>
  );
};

export default CodeQuizActions;
